import React from "react";
import { LeadCard, Sec } from "./parts";
import { lostView, callsView, callFacts, quoteView } from "./leadModel.mjs";
import { quoteSourceWords } from "../../timelineModel.mjs";
import { shortDate } from "../format.mjs";

/*
 * LOST: when and why, what we quoted, the last call's notes, and when it parks
 * itself (the brief's Lost row; matrix 1e: "When, why · quote · last call notes ·
 * parks itself in 90 days"). Undrawn as a frame, so built in the drawn card grammar
 * on Gabriel's ruling (2026-10-01). No sales actions.
 *
 * A loss turns into a park 90 days on (src/watchers/recycle.js), and a re-engage
 * date he set becomes that park's wake-up when it is later than the turn
 * (record/situation.mjs works both dates out; this block words them). Lost is
 * earned (lostGate.js: a call, pricing, and their no), so the quote and the last
 * call are nearly always there; when one is not, the block says so plainly.
 *
 * Props:
 *   record   the shell's bundle: view.situation (lost_at, parks_at, wakes_at),
 *            company.status_reason, deal (the quote kept), timeline.rows (the last
 *            call), zones
 *   ops      unused (no actions here)
 */
export default function Lost({ record }) {
  const v = lostView(record.view.situation, record.company, { now: record.now });
  const quote = quoteView(record.timeline.rows, { company: record.company });
  const quotedRow = (record.deal || []).find((r) => r.key === "quoted");
  const call = callsView(record.timeline.rows, { company: record.company, now: record.now })[0] || null;
  const d = (ts) => shortDate(ts, { now: record.now });

  return (
    <LeadCard label="Closed lost" meta={v.meta}>
      <div>
        <div className="rec-headline">{v.headline}</div>
        <div className="rec-body">{v.sub}</div>
      </div>
      <Sec>
        <span className="rec-lead__small">What we quoted{quote ? ` · ${lowerFirst(quoteSourceWords(quote, d))}` : ""}</span>
        {quote ? (
          <span className="rec-strong">{quote.terms.join(" · ")}</span>
        ) : quotedRow && !quotedRow.empty ? (
          <span>{quotedRow.value}</span>
        ) : (
          <span className="rec-sub">no pricing email on record</span>
        )}
      </Sec>
      <Sec>
        <span className="rec-lead__small">The last call{call ? ` · ${callFacts(call, record.zones, { now: record.now })}` : ""}</span>
        {!call && <span className="rec-sub">no call on record</span>}
        {call && call.points.length > 0 && (
          <ul className="rec-lead__points">
            {call.points.slice(0, 4).map((p, i) => (
              <li key={i}>
                {p.label ? <b>{p.label}: </b> : null}
                {p.text}
              </li>
            ))}
          </ul>
        )}
        {call && (call.todos.ours.length > 0 || call.todos.theirs.length > 0) && (
          <div className="rec-kv rec-kv--96">
            <span>Ours</span>
            <span>{call.todos.ours.join(" · ") || "nothing"}</span>
            <span>Theirs</span>
            <span>{call.todos.theirs.join(" · ") || "nothing"}</span>
          </div>
        )}
        {call && call.notes.map((n) => (
          <div key={n.id} className="rec-lead__notebody">{n.body}</div>
        ))}
        {call && !call.points.length && !call.notes.length && call.todos.ours.length + call.todos.theirs.length === 0 && (
          <span className="rec-sub">no notes from it</span>
        )}
      </Sec>
    </LeadCard>
  );
}

const lowerFirst = (s) => (s ? s.charAt(0).toLowerCase() + s.slice(1) : s);
